import React, { useEffect, useState } from "react";
//import "./orderdetails.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "../../../services/apiConfig";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../../services/axios.instance";
import AFLSuccessModal from "../../../components/AFL/AFLSuccessModal";
import AFLErrorModal from "../../../components/AFL/AFLErrorModal";
import { ImagePopupModal } from "../../../components";
import { toast } from "react-toastify";

const EditImages = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const OrderId = new URLSearchParams(location.search).get("OrderId");
  //const { orderDetails } = location.state;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || "1";
  const size = searchParams.get("size") || "10";

  const handleBack = () => {
    navigate({
      pathname: "/dashboard/servicelist",
      search: `?page=${page}&size=${size}`,
    });
  };

  interface DeliveryPartnerLocation {
    longitude: any;
    latitude: any;
    location: string;
    order_status: string;
    delivery_partner_status: string;
    changed_at: string;
    distance_in_km: number;
  }
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [checkListImageUrls, setCheckListImageUrls] = useState<string[]>([]);
  const [deletedImageUrls, setDeletedImageUrls] = useState<string[]>([]);
  const [recollectedCheckListImageUrls, setRecollectedCheckListImageUrls] =
    useState<string[]>([]);
  const [deletedRecollectedImageUrls, setDeletedRecollectedImageUrls] =
    useState<string[]>([]);
  const { data: orderDetails } = useQuery({
    queryKey: ["orderDetails", OrderId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URL.SERVICE_DETAILS}/${OrderId}`,
        {
          headers: {
            location: "0,0",
            language: "english",
          },
        }
      );
      return response.data;
    },
  });

  const formatDate = (isoDate: any) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };
  //--------------------------------------------------------------------------
  const driverReachedTimeDifference = (createdAt: string) => {
    const createdDate = new Date(createdAt);
    const driverReachedTime =
      orderDetails?.order_status_history_responses?.find(
        (status: any) =>
          status.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
      )?.status_changed_at;

    if (!driverReachedTime) return { totalMinutes: 0, formatted: "N/A" };

    const driverReachedDate = new Date(driverReachedTime);
    const driverDiffInSeconds = Math.floor(
      (driverReachedDate.getTime() - createdDate.getTime()) / 1000
    );

    const hours = Math.floor(driverDiffInSeconds / 3600);
    const minutes = Math.floor((driverDiffInSeconds % 3600) / 60);
    const totalMinutes = hours * 60 + minutes;

    return { totalMinutes, formatted: formatTime(hours, minutes) };
  };

  const timeDifference = (createdAt: string) => {
    const now = new Date();
    const createdDate = new Date(createdAt);
    const diffInSeconds = Math.floor(
      (now.getTime() - createdDate.getTime()) / 1000
    );

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const totalMinutes = hours * 60 + minutes;

    return { totalMinutes, formatted: formatTime(hours, minutes) };
  };

  const formatTime = (hours: number, minutes: number) => {
    if (hours > 0 && minutes > 0) {
      return `${hours} hour(s) ${minutes} minute(s) ago`;
    } else if (hours > 0) {
      return `${hours} hour(s) ago`;
    } else if (minutes > 0) {
      return `${minutes} minute(s) ago`;
    } else {
      return "Just now";
    }
  };
  // const { totalMinutes, formatted } = timeDifference(orderDetails?.createdAt);
  // const statusColor = totalMinutes < 100 ? "text-success" : "text-danger";
  const shouldUseTimeDifference =
    orderDetails?.aaa_service_request_model?.state === "initiate";
  const timeData = shouldUseTimeDifference
    ? timeDifference(orderDetails?.createdAt)
    : { totalMinutes: 0, formatted: "Just now" };

  // Determine status color based on conditions
  const statusColor = shouldUseTimeDifference
    ? timeData.totalMinutes < 10
      ? "text-success"
      : "text-danger"
    : orderDetails?.is_dispatched_within_time
    ? "text-success"
    : "text-danger";

  const { totalMinutes: totalMinutesDriverReached } =
    driverReachedTimeDifference(orderDetails?.createdAt);

  const driverReachedStatusColor =
    totalMinutesDriverReached < 60 ? "text-success" : "text-danger";

  // State for pickup location
  const [pickupLocation, setPickupLocation] = useState({
    name: "",
    latitude: "",
    longitude: "",
  });

  const [showImagePopupModal, setShowImagePopupModal] = useState(false);
  const [imageSource, setImageSource] = useState("");
  const onClickVdrDocument = (imageUrl: string) => {
    setImageSource(imageUrl);
    setShowImagePopupModal(true);
  };

  // Update details mutation
  const { mutate: updateDetails, isPending: isUpdating } = useMutation({
    mutationFn: async (data: {}) => {
      const response = await axiosInstance.put(API_URL.CHECKLIST_IMAGE_EDIT, {
        order_number: orderDetails?.order_service_details?.orderNumber,
        ...data,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["orderDetails", OrderId] });
      setShowSuccessModal(true);
    },
    onError: () => {
      setShowErrorModal(true);
    },
  });

  // Fetch delivery partner location
  const { data: deliveryPartnerData = [] } = useQuery<
    DeliveryPartnerLocation[]
  >({
    queryKey: [
      "deliveryPartnerLocation",
      orderDetails?.deliveryPartnerId,
      orderDetails?.order_service_details?.orderNumber,
    ],
    queryFn: async () => {
      if (!orderDetails?.order_service_details?.orderNumber) {
        return [];
      }

      const payload = orderDetails?.deliveryPartnerId
        ? {
            delivery_partner_id: orderDetails.deliveryPartnerId,
            order_number: orderDetails.order_service_details.orderNumber,
          }
        : {
            service_from_app: "false",
            order_number: orderDetails.order_service_details.orderNumber,
          };

      const response = await axiosInstance.post(
        `${API_URL.DRIVER_LOCATION}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data || [];
    },
    enabled: !!orderDetails?.order_service_details?.orderNumber,
  });

  // Set initial values when orderDetails or deliveryPartnerData changes
  useEffect(() => {
    if (orderDetails) {
      setCheckListImageUrls(
        orderDetails.products?.[0]?.checkListImageUrls || []
      );
      setRecollectedCheckListImageUrls(
        orderDetails.products?.[0]?.recollectedCheckListImageUrls || []
      );
    }
  }, [orderDetails]);

  // Update pickup location when delivery partner data is available
  useEffect(() => {
    if (deliveryPartnerData && deliveryPartnerData.length > 0) {
      const reachedLocation = deliveryPartnerData.find(
        (status) =>
          status.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
      );

      if (reachedLocation) {
        setPickupLocation({
          name: reachedLocation.location || "",
          latitude: reachedLocation.latitude || "",
          longitude: reachedLocation.longitude || "",
        });
      }
    }
  }, [deliveryPartnerData]);

  // Handle pickup location update
  const handleUpdatePickupLocation = () => {
    updateDetails({
      delivery_partner_location: [
        {
          latitude: pickupLocation.latitude,
          longitude: pickupLocation.longitude,
          delivery_partner_status: "REACHED_CUSTOMER_LOCATION",
          location: pickupLocation.name,
        },
      ],
    });
  };

  // Handle input change for pickup location
  const handlePickupLocationChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setPickupLocation((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  interface UpdateData {
    check_list_image_urls?: string[];
    recollected_check_list_image_urls?: string[];
  }

  const handleUpdate = () => {
    // // Validate checklist images
    // if (checkListImageUrls.length === 0) {
    //   toast.error("Please upload at least one checklist image");
    //   return;
    // }

    // if (recollectedCheckListImageUrls.length === 0) {
    //   toast.error("Please upload at least one recollected checklist image");
    //   return;
    // }

    const updateData: UpdateData = {};

    // Add image URLs if they exist
    if (checkListImageUrls.length > 0) {
      updateData.check_list_image_urls = checkListImageUrls;
    }
    if (recollectedCheckListImageUrls.length > 0) {
      updateData.recollected_check_list_image_urls =
        recollectedCheckListImageUrls;
    }

    // Make the API call with all updates
    if (Object.keys(updateData).length > 0) {
      updateDetails(updateData);
    }
  };

  // Delivery partner data is fetched in the earlier query with more comprehensive parameters

  const handleChecklistImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input value to allow re-uploading the same file
    event.target.value = "";

    const formData = new FormData();
    formData.append("product_image", file);

    try {
      const response = await axiosInstance.post(
        API_URL.PRODUCT_IMAGE_UPLOAD,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data) {
        setCheckListImageUrls((prev) => [...prev, response.data]);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const handleRecollectedChecklistImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input value to allow re-uploading the same file
    event.target.value = "";

    const formData = new FormData();
    formData.append("product_image", file);

    try {
      const response = await axiosInstance.post(
        API_URL.PRODUCT_IMAGE_UPLOAD,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data) {
        setRecollectedCheckListImageUrls((prev) => [...prev, response.data]);
      }
    } catch (error) {
      console.error("Error uploading recollected image:", error);
    }
  };

  const handleDeleteImage = (urlToDelete: string) => {
    setCheckListImageUrls((prev) => prev.filter((url) => url !== urlToDelete));
    setDeletedImageUrls((prev) => [...prev, urlToDelete]);
  };

  const handleDeleteRecollectedImage = (urlToDelete: string) => {
    setRecollectedCheckListImageUrls((prev) =>
      prev.filter((url) => url !== urlToDelete)
    );
    setDeletedRecollectedImageUrls((prev) => [...prev, urlToDelete]);
  };

  return (
    <div>
      <div className="col-12">
        <div className="row">
          <div className="col-md-12 text-start div-bg-invoice-details pt-3">
            <div className="card card-bg-invoice-details">
              <div className="card-body px-5">
                <div>
                  <button onClick={handleBack} className="all_button_style">
                    Back
                  </button>
                </div>
                <div className="row pb-3">
                  <div className="table-responsive pt-3">
                    <div className="mb-3 text-center invoice-text-yellow-afl">
                      <h4>
                        Job Reference No:{" "}
                        <span className="">
                          {orderDetails?.order_service_details?.referenceNumber}
                        </span>
                      </h4>
                    </div>
                    <div className="row pt-3">
                      {/* LEFT SIDE TABLE */}
                      <div className="col-md-6">
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <th>Vehicle Make</th>
                                <td>
                                  {
                                    orderDetails?.order_service_details
                                      ?.vehicleMake
                                  }
                                </td>
                              </tr>
                              <tr>
                                <th>Vehicle Model</th>
                                <td>
                                  {
                                    orderDetails?.order_service_details
                                      ?.carModel
                                  }
                                </td>
                              </tr>
                              <tr>
                                <th>Mobile</th>
                                <td>
                                  {
                                    orderDetails?.aaa_service_request_model
                                      ?.mobileNumber
                                  }
                                </td>
                              </tr>
                              <tr>
                                <th>Plate Number</th>
                                <td>
                                  {
                                    orderDetails?.aaa_service_request_model
                                      ?.plateNumber
                                  }
                                </td>
                              </tr>
                              <tr>
                                <th>Chassis Number</th>
                                <td>{orderDetails?.chassis_number}</td>
                              </tr>
                              <tr>
                                <th>Service Date</th>
                                <td>{formatDate(orderDetails?.createdAt)}</td>
                              </tr>

                              {orderDetails?.recollection_location ? (
                                <>
                                  <tr>
                                    <th>Location From</th>
                                    <td>
                                      {orderDetails?.delivery_location?.address}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>Location To</th>
                                    <td>
                                      {
                                        orderDetails?.recollection_location
                                          ?.address
                                      }
                                    </td>
                                  </tr>
                                </>
                              ) : (
                                <tr>
                                  <th>Location To</th>
                                  <td>
                                    {orderDetails?.delivery_location?.address}
                                  </td>
                                </tr>
                              )}

                              <tr>
                                <th>Comments AFL</th>
                                <td>
                                  {
                                    orderDetails?.order_service_details
                                      ?.aflComments
                                  }
                                </td>
                              </tr>

                              {orderDetails?.merchant_comment && (
                                <tr>
                                  <th>Comments</th>
                                  <td>{orderDetails?.merchant_comment}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* RIGHT SIDE TABLE */}
                      <div className="col-md-6">
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <th>Customer Name</th>
                                <td>{orderDetails?.customer_name}</td>
                              </tr>
                              <tr>
                                <th>Service Type</th>
                                <td>
                                  {
                                    orderDetails?.order_service_details
                                      ?.serviceName
                                  }
                                </td>
                              </tr>
                              <tr>
                                <th>Service Status</th>
                                <td>
                                  {(() => {
                                    const state =
                                      orderDetails?.aaa_service_request_model?.state?.toLowerCase();
                                    const isDriverReachedAndCancelled =
                                      orderDetails?.order_service_details
                                        ?.isDriverReachedAndCancelled;
                                    const isAFLUser = true; // Assuming this is in an AFL context since it's in the AFL folder

                                    // Handle AFL user cases
                                    if (
                                      isAFLUser &&
                                      state === "driver_cancel"
                                    ) {
                                      return isDriverReachedAndCancelled
                                        ? "R&C"
                                        : "Cancelled";
                                    }

                                    // Handle reached and cancelled cases for non-AFL users
                                    if (isDriverReachedAndCancelled) {
                                      if (state === "cancel") {
                                        return "R&C";
                                      }
                                    }

                                    // For non-AFL users, show 'Driver Cancel Pending' when state is 'driver_cancel'
                                    if (
                                      !isAFLUser &&
                                      state === "driver_cancel"
                                    ) {
                                      return "Driver Cancel Pending";
                                    }

                                    // For AFL users, show 'Cancelled' for other cancelled states
                                    if (
                                      isAFLUser &&
                                      (state === "cancel" ||
                                        state === "reached_and_cancelled")
                                    ) {
                                      return "Cancelled";
                                    }

                                    // Handle completed_by_driver state
                                    if (state === "completed_by_driver") {
                                      return "Completed";
                                    }

                                    // Default case - return the state as is with proper capitalization
                                    return state
                                      ? state.charAt(0).toUpperCase() +
                                          state.slice(1)
                                      : "";
                                  })()}
                                </td>
                              </tr>
                              <tr>
                                <th>Scheduled Date & Time (if any)</th>
                                <td>
                                  {formatDate(
                                    orderDetails?.order_service_details
                                      ?.scheduledFor
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th>Driver Name</th>
                                <td style={{ maxWidth: "100px" }}>
                                  {`${
                                    orderDetails?.aaa_service_request_model
                                      ?.driverName ??
                                    `${
                                      orderDetails?.delivery_boy_details
                                        ?.firstName || ""
                                    } ${
                                      orderDetails?.delivery_boy_details
                                        ?.lastName || ""
                                    }`
                                  }`.trim()}
                                </td>
                              </tr>
                              <tr>
                                <th>Driver Contact</th>
                                <td>
                                  {orderDetails?.aaa_service_request_model
                                    ?.driverPhone ??
                                    orderDetails?.delivery_boy_details
                                      ?.phoneNumber}
                                </td>
                              </tr>
                              <tr>
                                <th>Comments from AAA agent</th>
                                <td>
                                  {orderDetails?.aaa_service_request_model
                                    ?.comments?.length > 0 ? (
                                    orderDetails?.aaa_service_request_model?.comments.map(
                                      (comment: any, index: number) => (
                                        <div key={index}>
                                          {comment?.comment}
                                          <br />
                                        </div>
                                      )
                                    )
                                  ) : (
                                    <span>No comments available</span>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th>Initiated DT</th>
                                <td>
                                  {formatDate(orderDetails?.createdAt)}
                                  <span className="text-muted">
                                    <span className={statusColor}>
                                      {" "}
                                      : STATUS
                                    </span>
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <th>Driver Started DT</th>
                                <td>
                                  {orderDetails?.order_status_history_responses?.some(
                                    (status: any) =>
                                      status.delivery_partner_status ===
                                      "DELIVERY_ACCEPTED"
                                  )
                                    ? formatDate(
                                        orderDetails?.order_status_history_responses?.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            "DELIVERY_ACCEPTED"
                                        )?.status_changed_at
                                      )
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <th>Driver Reached DT</th>
                                <td>
                                  {orderDetails?.order_status_history_responses?.some(
                                    (status: any) =>
                                      status.delivery_partner_status ===
                                      "REACHED_CUSTOMER_LOCATION"
                                  ) ? (
                                    <>
                                      {formatDate(
                                        orderDetails?.order_status_history_responses?.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            "REACHED_CUSTOMER_LOCATION"
                                        )?.status_changed_at
                                      )}
                                      <span className="text-muted">
                                        <span
                                          className={driverReachedStatusColor}
                                        >
                                          {" "}
                                          : STATUS
                                        </span>
                                      </span>
                                    </>
                                  ) : (
                                    "N/A"
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th>Service Started DT</th>
                                <td>
                                  {orderDetails?.order_status_history_responses?.some(
                                    (status: any) =>
                                      status.delivery_partner_status ===
                                      (orderDetails?.order_service_details
                                        ?.serviceType === "DISTANCE_BASED"
                                        ? "VEHICLE_COLLECTED"
                                        : "SERVICE_STARTED")
                                  )
                                    ? formatDate(
                                        orderDetails?.order_status_history_responses?.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            (orderDetails?.order_service_details
                                              ?.serviceType === "DISTANCE_BASED"
                                              ? "VEHICLE_COLLECTED"
                                              : "SERVICE_STARTED")
                                        )?.status_changed_at
                                      )
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <th>Service Completed DT</th>
                                <td>
                                  {orderDetails?.order_status_history_responses?.some(
                                    (status: any) =>
                                      status.delivery_partner_status ===
                                      "COMPLETED"
                                  )
                                    ? formatDate(
                                        orderDetails?.order_status_history_responses?.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            "COMPLETED"
                                        )?.status_changed_at
                                      )
                                    : "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className=" pt-3">
                    {/* Checklist Section */}
                    <div className="col-md-12 invoice-div-data-afl mb-2">
                      Checklist <span className="merchantStar">*</span>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="pt-3">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={handleChecklistImageUpload}
                          />
                        </div>

                        {checkListImageUrls.map((url, index) => (
                          <div className="col-md-2 pt-3" key={index}>
                            {url?.endsWith(".pdf") ? (
                              <a
                                href={url}
                                download={`Checklist ${index + 1}.pdf`}
                              >
                                Download Document {index + 1}
                              </a>
                            ) : (
                              <div className="text-center border p-2 rounded">
                                <img
                                  src={url}
                                  onClick={() => onClickVdrDocument(url)}
                                  alt={`Checklist ${index + 1}`}
                                  style={{
                                    width: 75,
                                    height: 75,
                                    cursor: "pointer",
                                  }}
                                />
                                <a
                                  href={url}
                                  download={`Checklist ${index + 1}.jpg`}
                                  style={{
                                    display: "block",
                                    marginTop: "5px",
                                    fontSize: "14px",
                                  }}
                                >
                                  Download
                                </a>
                                <button
                                  onClick={() => handleDeleteImage(url)}
                                  className="btn btn-sm btn-danger mt-2"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Recollected Checklist Section */}
                  {orderDetails?.aaa_service_request_model?.state !==
                    "start" && (
                    <div className="pt-3">
                      <div className="col-md-12 invoice-div-data-afl mb-2">
                        Recollected Checklist{" "}
                        <span className="merchantStar">*</span>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="pt-3">
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={handleRecollectedChecklistImageUpload}
                            />
                          </div>
                        </div>
                        <div className="row">
                          {recollectedCheckListImageUrls.map((url, index) => (
                            <div
                              className="col-6 col-sm-4 col-md-2 pt-3"
                              key={index}
                            >
                              {url.endsWith(".pdf") ? (
                                <a
                                  href={url}
                                  download={`Recollected_Checklist_${
                                    index + 1
                                  }.pdf`}
                                  style={{
                                    display: "block",
                                    margin: "10px 0",
                                    fontSize: "12px",
                                  }}
                                >
                                  Download Document {index + 1}
                                </a>
                              ) : (
                                <div className="text-center border p-2 rounded">
                                  <img
                                    src={url}
                                    onClick={() => onClickVdrDocument(url)}
                                    alt={`Recollected Checklist ${index + 1}`}
                                    style={{
                                      width: 60,
                                      height: 60,
                                      cursor: "pointer",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <a
                                    href={url}
                                    download={`Recollected_Checklist_${
                                      index + 1
                                    }.jpg`}
                                    style={{
                                      display: "block",
                                      marginTop: "5px",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Download
                                  </a>
                                  <button
                                    onClick={() =>
                                      handleDeleteRecollectedImage(url)
                                    }
                                    className="btn btn-sm btn-danger mt-2"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row mt-4">
                    <div className="col-12 text-end">
                      <button
                        className="invoice-approve-btn px-4"
                        onClick={handleUpdate}
                        disabled={
                          isUpdating ||
                          !checkListImageUrls ||
                          !recollectedCheckListImageUrls
                        }
                      >
                        {isUpdating ? "Updating..." : "UPDATE"}
                      </button>
                    </div>
                  </div>
                  {/* Pickup Location Section */}
                  {!orderDetails?.deliveryPartnerId &&
                    ["done", "completed_by_driver"].includes(
                      orderDetails?.aaa_service_request_model?.state?.toLowerCase()
                    ) && (
                      <div className="col-md-12 pt-4">
                        <div className="card mb-4">
                          <div className="card-header">
                            <h5 className="mb-0">Reached Pickup Location</h5>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-4">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="name"
                                  placeholder="Location Name"
                                  value={pickupLocation.name}
                                  onChange={handlePickupLocationChange}
                                />
                              </div>
                              <div className="col-md-3">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="latitude"
                                  value={pickupLocation.latitude}
                                  onChange={handlePickupLocationChange}
                                  placeholder="Latitude"
                                />
                              </div>
                              <div className="col-md-3">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="longitude"
                                  value={pickupLocation.longitude}
                                  onChange={handlePickupLocationChange}
                                  placeholder="Longitude"
                                />
                              </div>
                              <div className="col-md-2 d-flex align-items-end">
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={handleUpdatePickupLocation}
                                  disabled={!pickupLocation.name || isUpdating}
                                >
                                  {isUpdating ? "Saving..." : "Save "}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImagePopupModal
        isOpen={showImagePopupModal}
        //  onClose={onCloseImagePopupModal}
        src={imageSource}
        onClose={() => setShowImagePopupModal(false)}
        message={""}
      />
      {/* Success Modal */}
      <AFLSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onNav={() => {
          navigate("/dashboard/servicelist");
          setShowSuccessModal(false);
        }}
        message="Details updated successfully"
      />
      {/* Error Modal */}
      <AFLErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message="Failed to update details. Please try again."
      />
    </div>
  );
};

export default EditImages;
